import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { confirmDialog } from '../../../utils/confirm';
import { Briefcase, Plus, Pencil, Trash2, Eye } from 'lucide-react';
import Button from '../../../components/Button';
import AddJobOpening from './AddJobOpening';
import ViewJobOpening from './ViewJobOpening';
import {
  getJobOpeningsAdmin,
  createJobOpening,
  updateJobOpening,
  deleteJobOpening,
} from '../../../api';
import PageLoader from '../../../components/PageLoader';
import Swal from 'sweetalert2';

const AdminJobOpenings = () => {
  const [jobs, setJobs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [viewingJob, setViewingJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      const list = await getJobOpeningsAdmin();
      setJobs(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load job openings');
      setJobs([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (payload, jobId) => {
    try {
      setError('');
      if (jobId) {
        await updateJobOpening(jobId, payload);
      } else {
        await createJobOpening(payload);
      }
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Job opening ${jobId ? 'updated' : 'created'} successfully`,
        confirmButtonColor: '#38915c',
      });
      await load(false);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to save job opening',
        confirmButtonColor: '#38915c',
      });
      return;
    }
    setModalOpen(false);
    setEditingJob(null);
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setModalOpen(true);
  };

  const handleView = (job) => {
    setViewingJob(job);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: 'Delete job opening?',
      text: 'This job will be removed from the Career page.',
    });
    if (!confirmed) return;
    try {
      await deleteJobOpening(id);
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Job opening deleted successfully',
        confirmButtonColor: '#38915c',
      });
      await load(false);
      if (editingJob?.id === id) {
        setEditingJob(null);
        setModalOpen(false);
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to delete job opening',
        confirmButtonColor: '#38915c',
      });
    }
  };

  const openAddModal = () => {
    setEditingJob(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingJob(null);
  };

  const closeDetailsModal = () => {
    setViewingJob(null);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <Briefcase className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">Manage</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Job Openings</h1>
          <p className="text-gray-500 text-sm mt-0.5">Jobs shown on the Career page. Add or edit listings.</p>
        </div>
        <Button variant="primary" size="sm" onClick={openAddModal} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Job
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      <AddJobOpening
        open={modalOpen}
        onClose={closeModal}
        onSuccess={handleSave}
        editJob={editingJob}
      />

      <ViewJobOpening 
        open={Boolean(viewingJob)}
        onClose={closeDetailsModal}
        job={viewingJob}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Title</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Department</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Location</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No job openings yet. Add one to show on the Career page.
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-800">{job.title}</td>
                    <td className="py-3 px-4 text-gray-600">{job.department}</td>
                    <td className="py-3 px-4 text-gray-600">{job.location}</td>
                    <td className="py-3 px-4 text-gray-600">{job.type || 'Full-time'}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${job.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {job.isActive ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip title="View Details">
                          <button
                            type="button"
                            onClick={() => handleView(job)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-secondary-600 hover:bg-secondary-50 hover:text-secondary-700 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <button
                            type="button"
                            onClick={() => handleEdit(job)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <button
                            type="button"
                            onClick={() => handleDelete(job.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminJobOpenings;
